import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Textarea,
    Dropdown,
    Option,
} from '@fluentui/react-components';
import { DialogOpenChangeEventHandler } from '@fluentui/react-dialog';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useScrollBarStyle } from './scrollbar.styles';
import IConnectToTeam from '../../../../../model/IConnectToTeam';
import { AutoReplyMessageType } from '../../../../../model/AutoReplyMessageType';

interface UpdateAutoReplyMessageDialogActionProps {
    open: boolean;
    allAutoReplies: IConnectToTeam[];
    groundTeam: string;
    autoReply: string;
    messageType: AutoReplyMessageType;
    loadStatus: string;
    onSaveBtnClick: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        data: {
            groundTeam: string;
            autoReply: string;
        }
    ) => void;
    onOpenChange?: DialogOpenChangeEventHandler;
}

const isDataValid = (
    groundTeam: string,
    autoReply: string,
    newGroundTeam: string,
    newAutoReply: string
): boolean => {
    if (!newGroundTeam || !newAutoReply) {
        return false;
    }
    if (groundTeam !== newGroundTeam || autoReply !== newAutoReply) {
        return true;
    }
    return false;
};

const UpdateAutoReplyMessageDialogAction = ({
    open,
    allAutoReplies,
    groundTeam,
    autoReply,
    messageType,
    loadStatus: connectToTeamsStatus,
    onSaveBtnClick,
    onOpenChange,
}: UpdateAutoReplyMessageDialogActionProps) => {
    const { t } = useTranslation();

    const [newGroundTeam, setNewGroundTeam] = useState<string>('');
    const [newAutoReply, setNewAutoReply] = useState<string>('');

    const scrollBarStyle = useScrollBarStyle();

    const messageTypeKey = messageType === AutoReplyMessageType.InitialReply ? 'InitialAutoReply' : 'ClosedConnectionAutoReply';
    const messageTypeName = t(`MessageType_${messageTypeKey}`);

    const checkAutoReplyExists = (rowKey: string): void => {
        const found = allAutoReplies.find((r) => r.key === rowKey);
        setNewAutoReply(found?.autoReply || '');
    };

    useEffect(() => {
        setNewGroundTeam(groundTeam);
        setNewAutoReply(autoReply);
    }, [groundTeam, autoReply]);

    useEffect(() => {
        if (!open) {
            setNewGroundTeam('');
            setNewAutoReply('');
        }
    }, [open]);

    const selectedTeam = allAutoReplies.find(team => team.key === newGroundTeam);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle
                        action={
                            <DialogTrigger action="close">
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                />
                            </DialogTrigger>
                        }
                    >
                        {t('EditAutoReplyDialog_Title').replace('{messageType}', messageTypeName)}
                    </DialogTitle>
                    <DialogContent className={scrollBarStyle}>
                        <Field
                            label={t(
                                'CreateOrUpdateAutoReplyDialog_GroundTeamField_Label'
                            )}
                            required
                        >
                            <Dropdown
                                value={selectedTeam ? `${selectedTeam.teamName} - ${selectedTeam.channelName}` : ''}

                                disabled={
                                    connectToTeamsStatus !== 'idle'
                                }
                                placeholder={
                                    t(
                                        'CreateOrUpdateAutoReplyDialog_GroundTeamField_Placeholder'
                                    )!
                                }
                                onOptionSelect={(_, data) => {
                                    checkAutoReplyExists(
                                        data.optionValue ?? ''
                                    );
                                    setNewGroundTeam(
                                        data.optionValue ?? ''
                                    );
                                }}
                            >
                                {allAutoReplies.map((team) => (
                                    <Option
                                        key={team.key}
                                        value={team.key}
                                    >
                                        {`${team.teamName} - ${team.channelName}`}
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>
                        <Field
                            label={messageTypeName}
                            required
                        >
                            <Textarea
                                appearance="outline"
                                resize="none"
                                value={newAutoReply}
                                onChange={(_, data) => {
                                    setNewAutoReply(data.value);
                                }}
                                rows={5}
                            />
                        </Field>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            appearance="primary"
                            disabled={
                                !isDataValid(
                                    groundTeam,
                                    autoReply,
                                    newGroundTeam,
                                    newAutoReply
                                )
                            }
                            onClick={(e) => {
                                onSaveBtnClick(e, {
                                    groundTeam: newGroundTeam,
                                    autoReply: newAutoReply,
                                });
                            }}
                        >
                            {t('Button_Common_Save')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default UpdateAutoReplyMessageDialogAction;
